import { useState } from 'react'
import { Link } from 'react-router-dom'

function formatVal(v) {
  if (v === null || v === undefined) return <span className="text-on-surface-variant/40 italic">empty</span>
  if (typeof v === 'boolean') return v ? 'true' : 'false'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

function actionBadgeStyle(action) {
  if (action === 'CREATE') return 'bg-emerald-100 text-emerald-800'
  if (action === 'UPDATE') return 'bg-blue-100 text-blue-800'
  if (action === 'ARCHIVE' || action === 'DELETE') return 'bg-amber-100 text-amber-800'
  return 'bg-purple-100 text-purple-800'
}

export default function MasterApprovalDiffModal({
  isOpen,
  detail,
  isLoading,
  error,
  returnUrl,
  onClose,
  onApprove,
  onReject,
  isBusy,
}) {
  const [reason, setReason] = useState('')
  const [showRawJson, setShowRawJson] = useState(false)

  if (!isOpen) return null

  const isPending = detail?.status === 'PENDING'
  const changedFields = detail?.changed_fields || []
  const hasChanges = changedFields.length > 0

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-[#191c1e]/50 backdrop-blur-xs p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-outline-variant/20 px-8 py-5 bg-surface-container-low/50">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-primary text-2xl">compare_arrows</span>
            <div>
              <h3 className="text-xl font-extrabold text-on-surface">
                {detail?.entity_name || 'Approval Request'}
              </h3>
              <p className="text-xs font-semibold text-on-surface-variant mt-0.5">
                {detail?.module_name || detail?.module_key} • Request #{detail?.id} • Rev {detail?.revision || 1}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {detail?.action && (
              <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${actionBadgeStyle(detail.action)}`}>
                {detail.action}
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-2 text-on-surface-variant transition-colors hover:bg-surface-container-high"
              aria-label="Close dialog"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
          {isLoading ? (
            <div className="py-20 text-center text-sm text-on-surface-variant">
              Loading request details and snapshot diff...
            </div>
          ) : error ? (
            <div className="rounded-xl border border-error/30 bg-error-container p-4 text-sm text-error">
              {error}
            </div>
          ) : (
            <>
              {/* Meta details strip */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 rounded-2xl bg-surface-container-low p-4 text-xs">
                <div>
                  <span className="text-on-surface-variant uppercase font-bold tracking-wider block">Requested By</span>
                  <span className="font-semibold text-on-surface mt-1 block">{detail?.requested_by_name || 'User'}</span>
                </div>
                <div>
                  <span className="text-on-surface-variant uppercase font-bold tracking-wider block">Requested At</span>
                  <span className="font-semibold text-on-surface mt-1 block">{detail?.requested_at || '-'}</span>
                </div>
                <div>
                  <span className="text-on-surface-variant uppercase font-bold tracking-wider block">Current Status</span>
                  <span className="font-bold text-primary mt-1 block">{detail?.status}</span>
                </div>
                <div>
                  <span className="text-on-surface-variant uppercase font-bold tracking-wider block">Reviewed By</span>
                  <span className="font-semibold text-on-surface mt-1 block">{detail?.reviewed_by_name || '-'}</span>
                </div>
              </div>

              {/* View mode toggle */}
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold uppercase tracking-wider text-on-surface-variant">
                  {detail?.action === 'CREATE' ? 'Proposed Record Data' : 'Field Comparison (Base vs. Proposed)'}
                </h4>
                <button
                  type="button"
                  onClick={() => setShowRawJson((prev) => !prev)}
                  className="text-xs font-semibold text-primary hover:underline"
                >
                  {showRawJson ? 'Switch to Formatted Table' : 'View Raw Snapshot JSON'}
                </button>
              </div>

              {showRawJson ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-bold text-on-surface-variant mb-2">Base Snapshot (Live):</p>
                    <pre className="max-h-80 overflow-auto rounded-xl bg-surface-container-low p-4 text-xs font-mono text-on-surface border border-outline-variant/20">
                      {JSON.stringify(detail?.base_snapshot || {}, null, 2)}
                    </pre>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-on-surface-variant mb-2">Proposed Payload:</p>
                    <pre className="max-h-80 overflow-auto rounded-xl bg-surface-container-low p-4 text-xs font-mono text-on-surface border border-outline-variant/20">
                      {JSON.stringify(detail?.proposed_payload || {}, null, 2)}
                    </pre>
                  </div>
                </div>
              ) : hasChanges ? (
                <div className="overflow-x-auto rounded-2xl border border-outline-variant/25">
                  <table className="w-full text-left text-sm border-collapse">
                    <thead>
                      <tr className="bg-surface-container-low/70 text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                        <th className="px-5 py-3.5">Field</th>
                        <th className="px-5 py-3.5">Current Live Value</th>
                        <th className="px-5 py-3.5">Proposed Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/15">
                      {changedFields.map((f) => (
                        <tr key={f.field} className="hover:bg-surface-container-lowest transition-colors">
                          <td className="px-5 py-3.5 font-bold text-primary">{f.label}</td>
                          <td className="px-5 py-3.5 bg-rose-50/40 text-rose-800 font-mono text-xs">
                            {formatVal(f.old_value)}
                          </td>
                          <td className="px-5 py-3.5 bg-emerald-50/40 text-emerald-800 font-mono text-xs font-bold">
                            {formatVal(f.new_value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : detail?.proposed_payload && Object.keys(detail.proposed_payload).length > 0 ? (
                <div className="overflow-x-auto rounded-2xl border border-outline-variant/25">
                  <table className="w-full text-left text-sm border-collapse">
                    <thead>
                      <tr className="bg-surface-container-low/70 text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                        <th className="px-5 py-3.5">Field Name</th>
                        <th className="px-5 py-3.5">Proposed Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/15">
                      {Object.entries(detail.proposed_payload).map(([k, v]) => (
                        <tr key={k} className="hover:bg-surface-container-lowest transition-colors">
                          <td className="px-5 py-3 font-semibold text-on-surface capitalize">
                            {k.replace(/_/g, ' ')}
                          </td>
                          <td className="px-5 py-3 font-mono text-xs text-primary font-bold">
                            {formatVal(v)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-8 text-center text-sm text-on-surface-variant">
                  No payload fields changed or captured.
                </div>
              )}

              {/* Decision reason input if pending */}
              {isPending && (
                <div className="pt-2">
                  <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-1.5">
                    Decision Remarks / Reason (Optional)
                  </label>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Enter approval note or rejection reasoning..."
                    className="w-full rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-2.5 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer controls */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-outline-variant/20 px-8 py-5 bg-surface-container-low/30">
          <div>
            {detail?.entry_route && (
              <Link
                to={`${detail.entry_route}${detail.entity_id ? `?id=${detail.entity_id}` : ''}${
                  detail.entry_route.includes('?') ? '&' : '?'
                }returnTo=${encodeURIComponent(returnUrl)}`}
                className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/40 bg-surface-container-high px-4 py-2.5 text-xs font-bold text-primary hover:bg-surface-container-highest transition shadow-xs"
              >
                <span className="material-symbols-outlined text-sm">open_in_new</span>
                Open Master Form
              </Link>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl px-5 py-2.5 text-xs font-bold text-on-surface-variant hover:bg-surface-container-high transition"
            >
              Close
            </button>
            {isPending && (
              <>
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => onReject(detail.id, reason)}
                  className="rounded-full bg-error-container hover:bg-error-container/80 px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-error disabled:opacity-50 transition"
                >
                  {isBusy ? 'Working...' : 'Reject'}
                </button>
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => onApprove(detail.id, reason)}
                  className="rounded-full bg-emerald-100 hover:bg-emerald-200 px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-emerald-700 disabled:opacity-50 transition"
                >
                  {isBusy ? 'Working...' : 'Approve'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
